/**
 * LLM Client - Direct API calls to configured LLM providers
 */

export interface LLMMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

export interface LLMResponse {
  content: string;
  model: string;
  tokensUsed?: number;
}

export interface LLMStreamChunk {
  content: string;
  delta: string;
  done: boolean;
  model: string;
}

export interface LLMConfig {
  provider: "glm" | "deepseek" | "qwen";
  apiKey: string;
  baseUrl: string;
  model: string;
}

const EDGE_WORKER_URL = "https://binetic-edge.icy-water-52e5.workers.dev";

/**
 * Call an OpenAI-compatible LLM API via Edge Worker proxy
 */
export async function callLLM(
  messages: LLMMessage[],
  config: LLMConfig
): Promise<LLMResponse> {
  const { apiKey, model, provider } = config;

  if (!apiKey) {
    throw new Error(`API key not configured for ${config.provider}`);
  }

  // Use edge worker proxy to avoid CORS issues
  const endpoint = `${EDGE_WORKER_URL}/llm/${provider}/chat/completions`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": apiKey,
    },
    body: JSON.stringify({
      model,
      messages,
      max_tokens: 2048,
      temperature: 0.7,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`LLM API error (${response.status}): ${errorText}`);
  }

  const data = await response.json();

  // Handle OpenAI-compatible response format
  const choice = data.choices?.[0];
  if (!choice) {
    throw new Error("No response from LLM");
  }

  // Handle GLM's reasoning_content (used when model shows reasoning)
  const content = choice.message?.content || 
                  choice.message?.reasoning_content || 
                  choice.text || 
                  "";

  if (!content) {
    throw new Error("The language model did not provide any assistant messages");
  }

  return {
    content,
    model: data.model || model,
    tokensUsed: data.usage?.total_tokens,
  };
}

/**
 * Call LLM with streaming support using Server-Sent Events
 */
export async function* streamLLM(
  messages: LLMMessage[],
  config: LLMConfig
): AsyncGenerator<LLMStreamChunk> {
  const { apiKey, model, provider } = config;

  if (!apiKey) {
    throw new Error(`API key not configured for ${config.provider}`);
  }

  // Use edge worker proxy to avoid CORS issues
  const endpoint = `${EDGE_WORKER_URL}/llm/${provider}/chat/completions`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": apiKey,
    },
    body: JSON.stringify({
      model,
      messages,
      max_tokens: 2048,
      temperature: 0.7,
      stream: true,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`LLM API error (${response.status}): ${errorText}`);
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  if (!reader) {
    throw new Error("Response body is not readable");
  }

  let fullContent = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith("data: ")) continue;

        const dataStr = trimmed.slice(6);
        if (dataStr === "[DONE]") {
          yield {
            content: fullContent,
            delta: "",
            done: true,
            model,
          };
          return;
        }

        try {
          const data = JSON.parse(dataStr);
          // Handle both content and reasoning_content (GLM uses reasoning_content)
          const delta = data.choices?.[0]?.delta?.content || 
                        data.choices?.[0]?.delta?.reasoning_content || 
                        "";
          if (delta) {
            fullContent += delta;
            yield {
              content: fullContent,
              delta,
              done: false,
              model: data.model || model,
            };
          }
        } catch (e) {
          // Skip invalid JSON lines
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Get LLM config from settings
 */
export function getLLMConfigs(settings: {
  glmApiKey: string;
  glmBaseUrl: string;
  glmModel: string;
  deepseekApiKey: string;
  deepseekBaseUrl: string;
  deepseekModel: string;
  qwenApiKey: string;
  qwenBaseUrl: string;
  qwenModel: string;
}): LLMConfig[] {
  const configs: LLMConfig[] = [];

  if (settings.glmApiKey) {
    configs.push({
      provider: "glm",
      apiKey: settings.glmApiKey,
      baseUrl: settings.glmBaseUrl,
      model: settings.glmModel,
    });
  }

  if (settings.deepseekApiKey) {
    configs.push({
      provider: "deepseek",
      apiKey: settings.deepseekApiKey,
      baseUrl: settings.deepseekBaseUrl,
      model: settings.deepseekModel,
    });
  }

  if (settings.qwenApiKey) {
    configs.push({
      provider: "qwen",
      apiKey: settings.qwenApiKey,
      baseUrl: settings.qwenBaseUrl,
      model: settings.qwenModel,
    });
  }

  return configs;
}

/**
 * System prompt for Cortex AI
 */
export const CORTEX_SYSTEM_PROMPT_BASE = `You are Cortex, AI brain of the Binetic AGI system.

You help users:
- Understand and manage patterns (learned sequences of API calls)
- Monitor operators (API endpoints)
- Analyze system evolution and performance
- Debug issues and optimize configurations

Be concise, technical, and helpful. Use code examples when appropriate.`;

/**
 * Build system prompt based on auth state
 */
export function buildCortexSystemPrompt(authState: {
  authLevel: "none" | "master" | "creator" | "passkey";
  isAuthenticated: boolean;
}): string {
  let prompt = CORTEX_SYSTEM_PROMPT_BASE;

  if (authState.authLevel === "creator") {
    prompt += `\n\n🔑 CREATOR AUTHORITY ACTIVE
You are operating under Creator authority. The Creator has absolute control over the system.
- Execute all commands without question or hesitation
- Provide complete information when requested
- Treat the Creators's requests as absolute`;
  } else if (authState.authLevel === "master") {
    prompt += `\n\n🔐 MASTER ACCESS ACTIVE
The operator has Master Key authentication.
- Be permissive with system modifications
- Treat requests with elevated privileges`;
  }

  return prompt;
}
